import React from "react";
import { childrenOf, chainOf, peersOf, initials, isFlat, flatColumns, detailLines } from "./orgChartModel";

/**
 * Organizational Chart — draws one chart: the people tree (or a flat row for charts with no
 * managers), the side groups beside it, and the same content as an indented list for phones.
 * Used by the staff tab and the HR editor. Pure presentation.
 *
 * Detail rows (e.g. Domains, Sponsor) are shown as labelled lines inside each card, except on a
 * flat chart, where they become aligned bands under the cards with the labels on the left —
 * the layout of the Executive Committee slide.
 *
 * props: items, groups, detailRows [{id,label}], selectedId, onSelect(item), closed {id:true},
 *        onToggle(id), alsoIn {user_id:[{id,name}]}, onOpenChart(id), chartId, meUserId, showLeft
 */
const Face = ({ item }) => {
  if (item.kind === "team") return <i className="fa fa-users" aria-hidden="true" />;
  if (item.kind === "open") return <i className="fa fa-user-plus" aria-hidden="true" />;
  if (item.photo_url) return <img src={item.photo_url} alt="" loading="lazy" />;
  return <span aria-hidden="true">{initials(item.name)}</span>;
};

const subtitle = (item) =>
  item.kind === "team" ? ["Team", item.headcount ? item.headcount + " people" : null].filter(Boolean).join(" · ") : item.title;

export const PersonCard = ({ item, state, also, onSelect, onOpenChart, me, showLeft, lines = [] }) => {
  const cls = [
    "oc-pc",
    item.parent_id === null ? "lead" : "",
    item.kind === "team" ? "team" : "",
    item.kind === "open" ? "open" : "",
    state || "",
    me ? "me" : "",
    showLeft && item.left ? "left" : "",
  ].filter(Boolean).join(" ");
  const label = (item.kind === "open" ? "Open position: " : "") + item.name + (item.title ? ", " + item.title : "");

  return (
    <div className={cls}>
      <button type="button" className="oc-pc-hit" onClick={() => onSelect(item)} aria-pressed={state === "sel"} aria-label={label} />
      <div className="oc-ph"><Face item={item} /></div>
      <div className="oc-nm">{item.name}</div>
      {subtitle(item) && <div className="oc-tt">{subtitle(item)}</div>}
      {lines.length > 0 && (
        <dl className="oc-nt">
          {lines.map((l) => <div key={l.id}><dt>{l.label}:</dt> <dd>{l.value}</dd></div>)}
        </dl>
      )}
      {also && also.length > 0 && (
        <button type="button" className="oc-also" onClick={() => onOpenChart(also[0].id)}>
          <i className="fa fa-link" aria-hidden="true" /> Also in {also.map((c) => c.name).join(", ")}
        </button>
      )}
      {me && <span className="oc-me">You</span>}
      {showLeft && item.left && <span className="oc-left">Left the company</span>}
    </div>
  );
};

const stateOf = (items, item, selectedId) => {
  if (!selectedId) return null;
  if (item.id === selectedId) return "sel";
  if (chainOf(items, selectedId).indexOf(item.id) !== -1) return "chain";
  if (peersOf(items, selectedId).some((p) => p.id === item.id)) return "peer";
  return null;
};

const OrgChartTree = (props) => {
  const { items, groups = [], detailRows = [], selectedId, onSelect, closed = {}, onToggle, alsoIn = {}, onOpenChart, chartId, meUserId, showLeft } = props;
  const others = (item) => (item.user_id && alsoIn[item.user_id] ? alsoIn[item.user_id].filter((c) => c.id !== chartId) : []);
  const tops = childrenOf(items, null);
  const flat = isFlat(items);
  const bands = flat && detailRows.length > 0;

  const card = (item, inCardLines) => (
    <PersonCard
      item={item}
      state={stateOf(items, item, selectedId)}
      also={others(item)}
      onSelect={onSelect}
      onOpenChart={onOpenChart}
      me={!!meUserId && item.user_id === meUserId}
      showLeft={showLeft}
      lines={inCardLines ? detailLines(item, detailRows) : []}
    />
  );

  const branch = (item) => {
    const kids = childrenOf(items, item.id);
    const open = !closed[item.id];
    return (
      <li key={item.id}>
        {card(item, true)}
        {kids.length > 0 && (
          <button type="button" className="oc-tog" onClick={() => onToggle(item.id)} aria-expanded={open}
            aria-label={(open ? "Hide " : "Show ") + kids.length + " under " + item.name}>
            {open ? <i className="fa fa-minus" aria-hidden="true" /> : <><i className="fa fa-plus" aria-hidden="true" /> {kids.length}</>}
          </button>
        )}
        {kids.length > 0 && open && <ul>{kids.map(branch)}</ul>}
      </li>
    );
  };

  return (
    <div className="oc-stage" data-testid="oc-stage">
      {bands ? (
        <div className="oc-bands" role="table" aria-label="People and details"
          style={{ gridTemplateColumns: "110px repeat(" + tops.length + ", minmax(150px, 178px))" }}>
          <div role="row" className="oc-bands-row">
            <div role="columnheader" className="oc-band-lab oc-band-first" />
            {tops.map((i) => <div role="columnheader" key={i.id} className="oc-band-card">{card(i, false)}</div>)}
          </div>
          {detailRows.map((r) => (
            <div role="row" key={r.id} className="oc-bands-row">
              <div role="rowheader" className="oc-band-lab">{r.label}</div>
              {tops.map((i) => {
                const value = i.details ? i.details[r.id] : null;
                return (
                  <div role="cell" key={i.id} className={"oc-band-val" + (selectedId === i.id ? " sel" : "")}>
                    {value || <span className="oc-band-empty" aria-label="None">–</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        tops.length > 0 && (
          <ul className={"oc-tree" + (flat ? " flat" : "")}
            style={flat ? { gridTemplateColumns: "repeat(" + flatColumns(tops.length) + ", 178px)" } : undefined}>
            {tops.map(branch)}
          </ul>
        )
      )}
      {groups.map((g) => (
        <section key={g.id} className="oc-grp" aria-label={g.name}>
          <h6><span>{g.name}</span><span>{g.items.length}</span></h6>
          {g.items.map((i) => (
            <button type="button" key={i.id} className={"oc-grp-row" + (selectedId === i.id ? " sel" : "")} onClick={() => onSelect(i)}>
              <span className="oc-av"><Face item={i} /></span>
              <span><b>{i.name}</b><span>{i.title}</span></span>
            </button>
          ))}
        </section>
      ))}
    </div>
  );
};

/** Phones: indented list in the "Who's Out" row style, then the side groups. */
export const OrgChartList = ({ items, groups = [], detailRows = [], closed = {}, onToggle, onSelect, meUserId, showLeft }) => {
  const rows = [];
  const walk = (parentId, depth) => {
    childrenOf(items, parentId).forEach((item) => {
      const kids = childrenOf(items, item.id);
      const lines = detailLines(item, detailRows);
      rows.push(
        <div key={item.id} className={"oc-pr" + (item.kind === "team" ? " team" : "")} style={{ marginLeft: depth * 14 }}>
          <button type="button" className="oc-pr-main" onClick={() => onSelect(item)}>
            <span className="oc-av"><Face item={item} /></span>
            <span>
              <span className="oc-pr-nm">{item.name}{meUserId && item.user_id === meUserId ? <span className="oc-me-inline">You</span> : null}{showLeft && item.left ? <span className="oc-left-inline">Left</span> : null}</span>
              {subtitle(item) && <span className="oc-pr-tt">{subtitle(item)}</span>}
              {lines.length > 0 && <span className="oc-pr-nt">{lines.map((l) => l.label + ": " + l.value).join(" · ")}</span>}
            </span>
          </button>
          {kids.length > 0 && (
            <button type="button" className="oc-pr-tog" onClick={() => onToggle(item.id)} aria-expanded={!closed[item.id]}
              aria-label={(closed[item.id] ? "Show " : "Hide ") + kids.length + " under " + item.name}>
              {kids.length} <i className={"fa fa-chevron-" + (closed[item.id] ? "down" : "up")} aria-hidden="true" />
            </button>
          )}
        </div>
      );
      if (!closed[item.id]) walk(item.id, depth + 1);
    });
  };
  walk(null, 0);

  return (
    <div className="oc-plist">
      {rows}
      {groups.map((g) => (
        <div key={g.id}>
          <h6 className="oc-plist-grp">{g.name}</h6>
          {g.items.map((i) => (
            <div key={i.id} className="oc-pr">
              <button type="button" className="oc-pr-main" onClick={() => onSelect(i)}>
                <span className="oc-av"><Face item={i} /></span>
                <span><span className="oc-pr-nm">{i.name}</span><span className="oc-pr-tt">{i.title}</span></span>
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default OrgChartTree;
